import React, { useState, useCallback } from 'react';
import API from '../../ApiService/ApiService';
import Context from './index';

const AiChatProvider = (props) => {
  const api = new API();

  // ── Overview stats
  const [aiOverview, setAiOverview] = useState(null);
  const [aiOverviewLoader, setAiOverviewLoader] = useState(false);

  // ── Chat logs
  const [aiLogs, setAiLogs] = useState([]);
  const [aiLogsLoader, setAiLogsLoader] = useState(false);
  const [aiLogsPaginator, setAiLogsPaginator] = useState({});
  const [aiLogsPage, setAiLogsPage] = useState(1);
  const [aiLogsDate, setAiLogsDate] = useState(null);
  const [aiLogsType, setAiLogsType] = useState(null);

  // ── Per-user logs
  const [aiUserLogs, setAiUserLogs] = useState([]);
  const [aiUserLogsLoader, setAiUserLogsLoader] = useState(false);
  const [aiUserSummary, setAiUserSummary] = useState({});

  // ── AI Settings
  const [aiSettings, setAiSettings] = useState(null);
  const [aiSettingsLoader, setAiSettingsLoader] = useState(false);

  // ── Access Rules
  const [accessRules, setAccessRules] = useState([]);
  const [ruleLoader, setRuleLoader] = useState(false);

  // ── Fetch overview
  const fetchAiOverview = useCallback(async () => {
    setAiOverviewLoader(true);
    await api.getAll('aichat', {}, (err, res) => {
      if (res?.data?.status === 200) {
        setAiOverview(res?.data?.data);
      } else {
        setAiOverview(null);
      }
      setAiOverviewLoader(false);
    });
  }, []);

  // ── Helper to handle R2 URLs (backend now handles signing)
  const fixR2Url = (url) => {
    if (!url) return url;
    // If it's already a full URL (like a signed URL), return it as is
    if (url.startsWith('http')) {
      return url;
    }
    // Fallback for legacy data that might only be a path
    const workingBase = 'https://ad2628fa62c279daef96367899bb25ee.r2.cloudflarestorage.com/rxdxapp/';
    return workingBase + url;
  };

  // ── Fetch logs (paginated)
  const fetchAiLogs = useCallback(async (page = 1, date = null, subscription_type = null) => {
    setAiLogsLoader(true);
    const params = { page, limit: 20 };
    if (date) params.date = date;
    if (subscription_type) params.subscription_type = subscription_type;
    await api.getAll('aichat/logs', params, (err, res) => {
      if (res?.data?.status === 200) {
        const rawData = res?.data?.data?.data || [];
        const mappedData = rawData.map(item => {
          // Backend already provides signed URLs in item.images
          return {
            ...item,
            image_url: item.images && item.images.length > 0 ? item.images[0].url : null
          };
        });
        // console.log(mappedData);
        setAiLogs(mappedData);
        setAiLogsPaginator(res?.data?.data?.paginator || {});
      } else {
        setAiLogs([]);
        setAiLogsPaginator({});
      }
      setAiLogsLoader(false);
    });
  }, []);

  // ── Fetch per-user logs
  const fetchAiUserLogs = useCallback(async (userId, page = 1) => {
    setAiUserLogsLoader(true);
    await api.getAll(`aichat/user/${userId}`, { page, limit: 20 }, (err, res) => {
      if (res?.data?.status === 200) {
        const rawLogs = res?.data?.data?.logs?.data || [];
        const mappedLogs = rawLogs.map(item => {
          // Backend already provides signed URLs in item.images
          return {
            ...item,
            image_url: item.images && item.images.length > 0 ? item.images[0].url : null
          };
        });
        setAiUserLogs(mappedLogs);
        setAiUserSummary(res?.data?.data?.summary || {});
      } else {
        setAiUserLogs([]);
        setAiUserSummary({});
      }
      setAiUserLogsLoader(false);
    });
  }, []);

  // ── Fetch settings
  const fetchAiSettings = useCallback(async () => {
    setAiSettingsLoader(true);
    await api.getAll('aichatsettings', {}, (err, res) => {
      if (res?.data?.status === 200) {
        setAiSettings(res?.data?.data);
      } else {
        setAiSettings(null);
      }
      setAiSettingsLoader(false);
    });
  }, []);

  // ── Update settings
  const updateAiSettings = useCallback(async (data, callback) => {
    setAiSettingsLoader(true);
    await api.create('aichatsettings', data, (err, res) => {
      setAiSettingsLoader(false);
      if (callback) callback(err, res);
    });
  }, []);

  // ── Access Rules methods
  const fetchAccessRules = useCallback(async () => {
    setRuleLoader(true);
    await api.getAll('aichataccess', {}, (err, res) => {
      if (res?.data?.status === 200) {
        setAccessRules(res?.data?.data || []);
      } else {
        setAccessRules([]);
      }
      setRuleLoader(false);
    });
  }, []);

  const updateAccessRule = useCallback(async (data) => {
    setRuleLoader(true);
    return new Promise((resolve, reject) => {
      api.create('aichataccess', data, (err, res) => {
        setRuleLoader(false);
        if (res?.data?.status === 200) {
          fetchAccessRules();
          resolve(res.data);
        } else {
          reject(err);
        }
      });
    });
  }, [fetchAccessRules]);

  const deleteAccessRule = useCallback(async (id) => {
    setRuleLoader(true);
    return new Promise((resolve, reject) => {
      api.remove('aichataccess', id, (err, res) => {
        setRuleLoader(false);
        if (res?.data?.status === 200) {
          fetchAccessRules();
          resolve(res.data);
        } else {
          reject(err);
        }
      });
    });
  }, [fetchAccessRules]);

  return (
    <Context.Provider value={{
      ...props,
      // Overview
      aiOverview, setAiOverview,
      aiOverviewLoader, setAiOverviewLoader,
      fetchAiOverview,
      // Logs
      aiLogs, setAiLogs,
      aiLogsLoader, setAiLogsLoader,
      aiLogsPaginator, setAiLogsPaginator,
      aiLogsPage, setAiLogsPage,
      aiLogsDate, setAiLogsDate,
      aiLogsType, setAiLogsType,
      fetchAiLogs,
      // User logs
      aiUserLogs, setAiUserLogs,
      aiUserLogsLoader, setAiUserLogsLoader,
      aiUserSummary, setAiUserSummary,
      fetchAiUserLogs,
      // Settings
      aiSettings, setAiSettings,
      aiSettingsLoader, setAiSettingsLoader,
      fetchAiSettings,
      updateAiSettings,
      // Access Rules
      accessRules,
      ruleLoader,
      fetchAccessRules,
      updateAccessRule,
      deleteAccessRule,
    }}>      {props.children}
    </Context.Provider>
  );
};

export default AiChatProvider;
